class DtcCartDrawer extends HTMLElement {
  connectedCallback() {
    this._quantityDebounceTimers = new Map();
    this.addEventListener('click', this._handleClick.bind(this));
    this.addEventListener('change', this._handleChange.bind(this));
    this.addEventListener('keyup', this._handleKeyup.bind(this));
    this.syncQuantityInputs();
  }

  syncQuantityInputs() {
    this.querySelectorAll('.dtc-cart-drawer__quantity-input').forEach((input) => {
      input.dataset.lastCommitted = String(Number(input.value) || 0);
    });
  }

  open(options = {}) {
    if (options.suppressViewCart) {
      this.dataset.vGtmSuppressViewCart = '1';
    }
    this.classList.add('active');
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
  }

  async _handleClick(event) {
    if (event.target.closest('[data-dtc-cart-close]')) {
      this.close();
      return;
    }

    const removeLink = event.target.closest('[data-dtc-cart-remove]');
    if (removeLink) {
      event.preventDefault();
      const line = Number(removeLink.dataset.line);
      const input = this.querySelector(`[data-line="${line}"].dtc-cart-drawer__quantity-input`);
      const lineEl = removeLink.closest('.dtc-cart-drawer__item');
      const currentValue = Number(
        input ? input.value : lineEl?.getAttribute('data-quantity') ?? 0
      );
      await window.DtcCartDrawerController.changeLine(line, 0, { previousValue: currentValue });
      return;
    }

    const quantityButton = event.target.closest('[data-dtc-cart-quantity-change]');
    if (!quantityButton) return;

    event.preventDefault();

    const line = Number(quantityButton.dataset.line);
    const input = this.querySelector(`[data-line="${line}"].dtc-cart-drawer__quantity-input`);
    const currentValue = Number(input ? input.value : 0);
    const nextValue = quantityButton.dataset.dtcCartQuantityChange === 'increase'
      ? currentValue + 1
      : Math.max(currentValue - 1, 0);

    if (nextValue === currentValue) return;

    const needsFullRefresh = nextValue === 0;
    if (!needsFullRefresh && input) {
      input.value = nextValue;
    }

    await window.DtcCartDrawerController.changeLine(line, nextValue, {
      previousValue: currentValue,
      optimistic: !needsFullRefresh,
    });
  }

  _handleChange(event) {
    const input = event.target.closest('.dtc-cart-drawer__quantity-input');
    if (!input) return;

    const line = Number(input.dataset.line);
    const lineEl = input.closest('.dtc-cart-drawer__item');
    const previousValue = Number(
      input.dataset.lastCommitted ?? lineEl?.dataset.quantity ?? input.getAttribute('value') ?? 0
    );

    clearTimeout(this._quantityDebounceTimers.get(line));
    this._quantityDebounceTimers.set(
      line,
      setTimeout(async () => {
        const value = Math.max(Number(input.value), 0);
        if (value === previousValue) return;

        const needsFullRefresh = value === 0;
        if (!needsFullRefresh) {
          input.value = value;
        }

        await window.DtcCartDrawerController.changeLine(line, value, {
          previousValue,
          optimistic: !needsFullRefresh,
        });

        if (!needsFullRefresh) {
          input.dataset.lastCommitted = String(value);
        }
      }, typeof ON_CHANGE_DEBOUNCE_TIMER !== 'undefined' ? ON_CHANGE_DEBOUNCE_TIMER : 300)
    );
  }

  _handleKeyup(event) {
    if (event.code === 'Escape') {
      this.close();
    }
  }
}

if (!customElements.get('dtc-cart-drawer')) {
  customElements.define('dtc-cart-drawer', DtcCartDrawer);
}

document.addEventListener('click', (event) => {
  const checkoutBtn = event.target.closest('#DtcCartDrawer-Checkout');
  if (!checkoutBtn || checkoutBtn.disabled) return;

  const form = document.getElementById('DtcCartDrawer-Form');
  if (!form) return;

  event.preventDefault();

  if (typeof form.requestSubmit === 'function') {
    form.requestSubmit(checkoutBtn);
    return;
  }

  form.submit();
});

/**
 * swiper-slider — initializes Swiper from slider-config JSON.
 * Migrated from assets/theme.js; loads after theme.js (defer order).
 */
if (!customElements.get('swiper-slider')) {
  class BaseSwiper extends HTMLElement {
    constructor() {
      super();
      this.LOADED = false;
      this.LOAD_WHEN_IN_VIEW = this.hasAttribute('load-when-in-view');
      this.DISABLE_ON_BREAKPOINT = this.hasAttribute('disable-on-breakpoint');
      this.BREAKPOINT = parseInt(this.getAttribute('disable-on-breakpoint'), 10);
      this._onResize = this.handleResize.bind(this);
      this._onSectionLoad = this._handleSectionLoad.bind(this);
    }

    connectedCallback() {
      this.swiperEl = this.querySelector('.swiper');
      const configAttr = this.getAttribute('slider-config');
      if (!configAttr || !this.swiperEl) return;

      try {
        this.getConfig = JSON.parse(configAttr);
      } catch (error) {
        console.warn('swiper-slider: invalid slider-config', error);
        return;
      }

      this.swiperNav = this.querySelector('[swiper-nav]');
      this.config = this._buildConfig();

      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        document.addEventListener('shopify:section:load', this._onSectionLoad);
      }

      if (this.DISABLE_ON_BREAKPOINT) {
        this.setAttribute('disabled', '');
        this.handleResize();
        window.addEventListener('resize', this._onResize);
        return;
      }

      if (this.LOAD_WHEN_IN_VIEW) {
        this._observeWhenInView();
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
      window.removeEventListener('resize', this._onResize);
      document.removeEventListener('shopify:section:load', this._onSectionLoad);
    }

    _buildConfig() {
      const modules = [];
      if (typeof Navigation !== 'undefined') modules.push(Navigation);
      if (typeof Pagination !== 'undefined') modules.push(Pagination);
      if (typeof A11y !== 'undefined') modules.push(A11y);
      if (typeof Autoplay !== 'undefined') modules.push(Autoplay);
      if (typeof EffectFade !== 'undefined' && this.getConfig?.effect === 'fade') {
        modules.push(EffectFade);
      }

      return {
        ...(modules.length ? { modules } : {}),
        watchSlidesProgress: true,
        a11y: {
          enabled: true,
          prevSlideMessage: 'Previous slide',
          nextSlideMessage: 'Next slide',
          firstSlideMessage: 'This is the first slide',
          lastSlideMessage: 'This is the last slide',
          paginationBulletMessage: 'Go to slide {{index}}',
        },
        navigation: {
          nextEl: this.querySelector('[swiper-button-next]'),
          prevEl: this.querySelector('[swiper-button-prev]'),
        },
        pagination: {
          el: this.querySelector('[swiper-pagination]'),
          type: 'fraction',
        },
        ...this.getConfig,
      };
    }

    init() {
      if (!this.swiperEl || typeof Swiper === 'undefined') return;
      if (this.swiper) {
        this.swiper.destroy(true, true);
      }
      this.swiper = new Swiper(this.swiperEl, this.config);
    }

    handleResize() {
      const windowWidth = window.innerWidth;

      if (windowWidth > this.BREAKPOINT && this.LOADED) {
        this.setAttribute('disabled', '');
        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
          this.LOADED = false;
        }
        if (this.swiperNav) this.swiperNav.classList.add('hidden');
        return;
      }

      if (windowWidth <= this.BREAKPOINT && !this.LOADED) {
        this.removeAttribute('disabled');
        if (this.swiperNav) this.swiperNav.classList.remove('hidden');
        this.init();
        this.LOADED = true;
      }
    }

    _observeWhenInView() {
      const options = { rootMargin: '0px 0px -30px 0px' };
      this.intersectionObserver = new IntersectionObserver((entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.swiperEl = this.querySelector('.swiper');
        this.init();
      }, options);
      this.intersectionObserver.observe(this);
    }

    _handleSectionLoad(event) {
      if (!this.isConnected) return;

      const section = this.closest('[id^="shopify-section-"]');
      if (!section || section.id !== 'shopify-section-' + event.detail.sectionId) return;

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      this.LOADED = false;
      this.swiperEl = this.querySelector('.swiper');
      this.config = this._buildConfig();

      if (this.DISABLE_ON_BREAKPOINT) {
        this.handleResize();
      } else if (this.LOAD_WHEN_IN_VIEW) {
        this._observeWhenInView();
      } else {
        this.init();
      }
    }
  }

  class SwiperSlider extends BaseSwiper {}

  customElements.define('swiper-slider', SwiperSlider);
}

/**
 * dtc-multicolumn — grid/slider mode helpers + overlay video cards.
 * Migrated from sections/dtc-multicolumn.liquid.
 */
if (!customElements.get('dtc-multicolumn')) {
  class DtcMulticolumn extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      const mode = this.dataset.sliderMode;
      const showNav = this.dataset.showNavigation === 'true';
      const extendRight = this.dataset.extendRight === 'true';
      const sectionId = this.id.replace('dtc-multicolumn-', '');

      if (extendRight && sectionId) {
        this._offsetProp = '--dtc-mc-left-offset-' + sectionId;
        this._updateLeftOffset = () => {
          const rect = this.getBoundingClientRect();
          document.documentElement.style.setProperty(
            this._offsetProp,
            rect.left + window.scrollX + 'px'
          );
        };
        this._updateLeftOffset();
        window.addEventListener('resize', this._updateLeftOffset, { passive: true });
      }

      if (mode === 'mobile_only' || mode === 'pc_only') {
        const grid = this.querySelector('[id^="dtc-multicolumn-grid-"]');
        if (grid) {
          this._grid = grid;
          this._stripInlineStyles = () => {
            if (mode === 'mobile_only' && window.innerWidth < 1000) return;
            if (mode === 'pc_only' && window.innerWidth >= 1000) return;
            grid.querySelectorAll('.swiper-slide').forEach((slide) => {
              slide.style.removeProperty('width');
              slide.style.removeProperty('margin-right');
            });
          };
          this._stripInlineStyles();
          this._styleObserver = new MutationObserver(() => this._stripInlineStyles());
          this._styleObserver.observe(grid, {
            attributes: true,
            subtree: true,
            attributeFilter: ['style'],
          });
          this._onResizeStrip = () => this._stripInlineStyles();
          window.addEventListener('resize', this._onResizeStrip, { passive: true });
        }
      }

      this._waitForSwiper(showNav);
    }

    disconnectedCallback() {
      if (this._updateLeftOffset) {
        window.removeEventListener('resize', this._updateLeftOffset);
      }
      if (this._onResizeStrip) {
        window.removeEventListener('resize', this._onResizeStrip);
      }
      if (this._styleObserver) this._styleObserver.disconnect();
      if (this._swiperTimer) clearInterval(this._swiperTimer);
      this._initialized = false;
    }

    _bindNavSync(swiper) {
      const btnPrev = this.querySelector('.dtc-multicolumn__nav-btn--prev');
      const btnNext = this.querySelector('.dtc-multicolumn__nav-btn--next');
      if (!btnPrev && !btnNext) return;

      const syncDisabled = () => {
        if (btnPrev) btnPrev.disabled = swiper.isBeginning && !swiper.params.loop;
        if (btnNext) btnNext.disabled = swiper.isEnd && !swiper.params.loop;
      };

      swiper.on('slideChange', syncDisabled);
      swiper.on('reachBeginning', syncDisabled);
      swiper.on('reachEnd', syncDisabled);
      syncDisabled();
    }

    _waitForSwiper(showNav) {
      const swiperEl = this.querySelector('.swiper');
      if (!swiperEl) return;

      const setup = () => {
        const swiper = swiperEl.swiper;
        if (!swiper) return;
        if (showNav) this._bindNavSync(swiper);
      };

      if (swiperEl.swiper) {
        setup();
        return;
      }

      let attempts = 0;
      this._swiperTimer = setInterval(() => {
        attempts += 1;
        if (swiperEl.swiper) {
          clearInterval(this._swiperTimer);
          this._swiperTimer = null;
          setup();
        } else if (attempts > 50) {
          clearInterval(this._swiperTimer);
          this._swiperTimer = null;
        }
      }, 100);
    }
  }

  customElements.define('dtc-multicolumn', DtcMulticolumn);
}

(function initDtcMulticolumnOverlayCards() {
  if (window.__dtcMulticolumnOverlayInit) return;
  window.__dtcMulticolumnOverlayInit = true;

  function parseSources(card, playBtn) {
    const raw = card.dataset.videoSources || (playBtn && playBtn.dataset.videoSources) || '[]';
    try {
      return JSON.parse(raw);
    } catch {
      return [];
    }
  }

  function loadSources(video, sources) {
    video.innerHTML = '';
    sources.forEach((source) => {
      const src = document.createElement('source');
      src.src = source.url;
      src.type = source.type;
      video.appendChild(src);
    });
  }

  function isCardVisible(card) {
    return card._dtcOverlayVisible === true;
  }

  function attachCard(card) {
    if (card._vOverlayInit) return;
    card._vOverlayInit = true;

    const playBtn = card.querySelector('.dtc-overlay-card__play');
    const videoWrap = card.querySelector('.dtc-overlay-card__video-wrap');
    const video = card.querySelector('.dtc-overlay-card__video');
    const autoplay = card.dataset.videoAutoplay === 'true';

    if (playBtn && playBtn.dataset.videoEmbedUrl) {
      playBtn.addEventListener('click', () => {
        const embedUrl = playBtn.dataset.videoEmbedUrl;
        const sectionEl = card.closest('[id^="shopify-section-"]');
        const sectionId = sectionEl ? sectionEl.id.replace('shopify-section-', '') : '';
        const modal = document.getElementById('dtc-ext-video-modal-' + sectionId);
        const iframeEl = document.getElementById('dtc-ext-video-iframe-' + sectionId);
        const nativeVideo = document.getElementById('dtc-ext-video-native-' + sectionId);
        if (!modal || !iframeEl) return;
        if (nativeVideo) {
          nativeVideo.pause();
          nativeVideo.setAttribute('hidden', '');
        }
        iframeEl.removeAttribute('hidden');
        iframeEl.src = embedUrl;
        modal.removeAttribute('hidden');
        document.body.style.overflow = 'hidden';
        card.classList.add('dtc-overlay-card--playing');
        modal._sourceCard = card;
      });
      return;
    }

    if (!videoWrap || !video) return;

    if (!autoplay && playBtn && playBtn.dataset.videoSources) {
      playBtn.addEventListener('click', () => {
        const sources = parseSources(card, playBtn);
        if (!sources.length) return;
        const sectionEl = card.closest('[id^="shopify-section-"]');
        const sectionId = sectionEl ? sectionEl.id.replace('shopify-section-', '') : '';
        const modal = document.getElementById('dtc-ext-video-modal-' + sectionId);
        const nativeVideo = document.getElementById('dtc-ext-video-native-' + sectionId);
        const iframeEl = document.getElementById('dtc-ext-video-iframe-' + sectionId);
        if (!modal || !nativeVideo) return;
        if (iframeEl) {
          iframeEl.src = '';
          iframeEl.setAttribute('hidden', '');
        }
        nativeVideo.innerHTML = '';
        sources.forEach((source) => {
          const src = document.createElement('source');
          src.src = source.url;
          src.type = source.type;
          nativeVideo.appendChild(src);
        });
        nativeVideo.removeAttribute('hidden');
        modal.removeAttribute('hidden');
        document.body.style.overflow = 'hidden';
        card.classList.add('dtc-overlay-card--playing');
        modal._sourceCard = card;
        nativeVideo.load();
        nativeVideo.play().catch(() => {});
      });
      return;
    }

    function startVideo(opts) {
      const sources = parseSources(card, playBtn);
      if (!sources.length) return;

      if (!video.querySelector('source')) loadSources(video, sources);
      if (opts && opts.muted) video.muted = true;
      else if (!autoplay) video.removeAttribute('muted');

      videoWrap.removeAttribute('hidden');
      video.load();
      video.play().catch(() => {});
      card.classList.add('dtc-overlay-card--playing');
    }

    function stopVideo() {
      video.pause();
      if (!autoplay) {
        video.innerHTML = '';
        videoWrap.setAttribute('hidden', '');
      }
      card.classList.remove('dtc-overlay-card--playing');
    }

    const closeBtn = videoWrap.querySelector('.dtc-overlay-card__video-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        stopVideo();
        if (autoplay) {
          card.classList.add('dtc-overlay-card--video-dismissed');
          videoWrap.setAttribute('hidden', '');
        }
      });
    }

    if (autoplay) {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function syncAutoplay() {
        if (prefersReducedMotion) return;
        if (card.classList.contains('dtc-overlay-card--video-dismissed')) return;
        if (isCardVisible(card)) {
          startVideo({ muted: true });
        } else {
          video.pause();
          if (card.classList.contains('dtc-overlay-card--video-dismissed')) {
            card.classList.remove('dtc-overlay-card--video-dismissed');
            videoWrap.removeAttribute('hidden');
          }
        }
      }

      if (!card._dtcOverlayIo) {
        card._dtcOverlayIo = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              card._dtcOverlayVisible = entry.isIntersecting && entry.intersectionRatio >= 0.25;
              syncAutoplay();
            });
          },
          { threshold: [0, 0.25, 0.5, 1] }
        );
        card._dtcOverlayIo.observe(card);
      }

      const swiperEl = card.closest('.swiper');
      if (swiperEl) {
        const bindSwiper = () => {
          const swiper = swiperEl.swiper;
          if (!swiper) return;
          swiper.on('slideChangeTransitionEnd', syncAutoplay);
        };
        if (swiperEl.swiper) bindSwiper();
        else {
          let attempts = 0;
          const timer = setInterval(() => {
            attempts += 1;
            if (swiperEl.swiper) {
              clearInterval(timer);
              bindSwiper();
            } else if (attempts > 50) {
              clearInterval(timer);
            }
          }, 100);
        }
      }

      syncAutoplay();
    }
  }

  function scanCards(root) {
    root.querySelectorAll('.dtc-overlay-card').forEach(attachCard);
  }

  function initExtVideoModals() {
    document.querySelectorAll('.dtc-ext-video-modal').forEach((modal) => {
      if (modal._dtcModalInit) return;
      modal._dtcModalInit = true;

      const iframeEl = modal.querySelector('.dtc-ext-video-modal__iframe');
      const nativeVideo = modal.querySelector('.dtc-ext-video-modal__video');
      const closeBtn = modal.querySelector('.dtc-ext-video-modal__close');
      const backdrop = modal.querySelector('.dtc-ext-video-modal__backdrop');

      function closeModal() {
        if (iframeEl) {
          iframeEl.src = '';
          iframeEl.setAttribute('hidden', '');
        }
        if (nativeVideo) {
          nativeVideo.pause();
          nativeVideo.innerHTML = '';
          nativeVideo.setAttribute('hidden', '');
        }
        modal.setAttribute('hidden', '');
        document.body.style.overflow = '';
        if (modal._sourceCard) {
          modal._sourceCard.classList.remove('dtc-overlay-card--playing');
          modal._sourceCard = null;
        }
      }

      if (closeBtn) closeBtn.addEventListener('click', closeModal);
      if (backdrop) backdrop.addEventListener('click', closeModal);
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !modal.hasAttribute('hidden')) closeModal();
      });
    });
  }

  function init(root) {
    scanCards(root || document);
    initExtVideoModals();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.classList?.contains('dtc-overlay-card')) attachCard(node);
        else scanCards(node);
        if (node.classList?.contains('dtc-ext-video-modal') || node.querySelector?.('.dtc-ext-video-modal')) {
          initExtVideoModals();
        }
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const section = document.getElementById('shopify-section-' + event.detail.sectionId);
      if (section) init(section);
    });
  }
})();

window.DtcCartDrawerController = {
  sectionName: 'dtc-cart-drawer',
  _changeLineQueue: Promise.resolve(),
  _linePending: new Map(),
  _lineFlushTimer: null,
  _flushRunning: false,
  _lineFlushDelay: 32,
  _loadingCount: 0,

  setDrawerLoading(busy) {
    const drawer = document.querySelector('dtc-cart-drawer');
    if (!drawer) return;

    if (busy) {
      this._loadingCount += 1;
    } else {
      this._loadingCount = Math.max(0, this._loadingCount - 1);
    }

    const isLoading = this._loadingCount > 0;
    drawer.classList.toggle('is-loading', isLoading);

    const loading = drawer.querySelector('.dtc-cart-drawer__loading');
    if (loading) loading.setAttribute('aria-hidden', isLoading ? 'false' : 'true');

    const main = drawer.querySelector('.dtc-cart-drawer__main');
    if (main) main.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  },

  async add(variantId, quantity = 1) {
    this.open({ suppressViewCart: true });
    this.setDrawerLoading(true);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity,
          sections: [this.sectionName],
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Unable to add this item to the cart.');
      }

      const data = await response.json();
      await this.refresh(data.sections);
      await this.syncCartBubble();
      return data.items && data.items.length ? data.items[0] : data;
    } finally {
      this.setDrawerLoading(false);
    }
  },

  changeLine(line, quantity, options = {}) {
    this._linePending.set(line, { quantity, options });

    clearTimeout(this._lineFlushTimer);
    this._lineFlushTimer = setTimeout(() => {
      this._lineFlushTimer = null;
      this._flushLineUpdates();
    }, this._lineFlushDelay);

    return this._changeLineQueue;
  },

  _flushLineUpdates() {
    this._changeLineQueue = this._changeLineQueue.catch(() => {}).then(() => this._runLineFlush());
    return this._changeLineQueue;
  },

  async _runLineFlush() {
    if (this._flushRunning) return;

    this._flushRunning = true;

    try {
      while (this._linePending.size > 0) {
        const entries = [...this._linePending.entries()];
        this._linePending.clear();

        for (const [line, payload] of entries) {
          await this._changeLineImpl(line, payload.quantity, payload.options);
        }
      }
    } finally {
      this._flushRunning = false;

      if (this._linePending.size > 0) {
        clearTimeout(this._lineFlushTimer);
        this._lineFlushTimer = setTimeout(() => {
          this._lineFlushTimer = null;
          this._flushLineUpdates();
        }, this._lineFlushDelay);
      }
    }
  },

  async _changeLineImpl(line, quantity, { previousValue, optimistic = false } = {}) {
    const needsSectionRender = quantity === 0;
    const gtmLineEl = needsSectionRender ? document.getElementById(`DtcCartDrawer-Item-${line}`) : null;

    if (needsSectionRender) {
      this.setDrawerLoading(true);
    }

    const body = { line, quantity };
    if (needsSectionRender) {
      body.sections = [this.sectionName];
      body.sections_url = window.location.pathname;
    }

    let data;
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });

      data = await response.json();

      if (!response.ok) {
        this._rollbackQuantity(line, previousValue, optimistic);
        this.showError(data.description || 'Unable to update the cart.');
        return;
      }

      this._emitLineChangeGtm(line, quantity, previousValue, data, gtmLineEl);

      if (needsSectionRender || data.item_count === 0) {
        await this.refresh(data.sections || (await this._fetchSections()));
        return;
      }

      this.syncCartState(data, line);
      this.updateCartBubble(data);
      this._reconcileLineQuantity(line, data);
      this.clearError();
    } catch {
      this._rollbackQuantity(line, previousValue, optimistic);
      this.showError('Unable to update the cart.');
    } finally {
      if (needsSectionRender) {
        this.setDrawerLoading(false);
      }
    }
  },

  _reconcileLineQuantity(line, cart) {
    if (this._linePending.has(line)) return;

    const item = cart.items[line - 1];
    const input = document.querySelector(`[data-line="${line}"].dtc-cart-drawer__quantity-input`);
    if (!item || !input) return;

    if (Number(input.value) !== item.quantity) {
      input.value = item.quantity;
    }

    input.dataset.lastCommitted = String(item.quantity);
  },

  _rollbackQuantity(line, previousValue, optimistic) {
    if (!optimistic || previousValue === undefined || this._linePending.has(line)) return;

    const input = document.querySelector(`[data-line="${line}"].dtc-cart-drawer__quantity-input`);
    if (input) input.value = previousValue;
  },

  _emitLineChangeGtm(line, quantity, previousValue, cartData, lineEl) {
    if (!window.vGtm?.cart?.trackLineChange) return;

    const prev = Number(previousValue);
    const next = Number(quantity);
    if (Number.isNaN(prev)) return;

    if (next <= 0 && prev > 0) {
      const source = lineEl || cartData?.items?.[line - 1];
      if (source) window.vGtm.cart.trackLineChange(source, prev, next, 'Cart Popup');
      return;
    }

    const current = cartData?.items?.[line - 1];
    if (current) window.vGtm.cart.trackLineChange(current, prev, next, 'Cart Popup');
  },

  formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'USD';

    try {
      let formatted = new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
      if (amount % 1 === 0) {
        formatted = formatted.replace(/([.,]00)(?!\d)/, '');
      }
      return formatted;
    } catch {
      const value = amount % 1 === 0 ? String(amount) : amount.toFixed(2);
      return `$${value}`;
    }
  },

  syncCartState(cart, line) {
    const drawer = document.querySelector('dtc-cart-drawer');
    if (!drawer) return;

    const subtotal = drawer.querySelector('.dtc-cart-drawer__subtotal-value');
    if (subtotal) subtotal.textContent = this.formatMoney(cart.total_price);

    const count = drawer.querySelector('.dtc-cart-drawer__count');
    if (count) {
      const itemCount = cart.item_count;
      count.textContent = `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`;
    }

    const checkout = drawer.querySelector('#DtcCartDrawer-Checkout');
    if (checkout) checkout.disabled = cart.item_count === 0;

    const item = cart.items[line - 1];
    if (!item) return;

    const lineEl = document.getElementById(`DtcCartDrawer-Item-${line}`);
    if (!lineEl) return;

    lineEl.dataset.quantity = String(item.quantity);

    this._updateLinePrice(lineEl, item);
  },

  _updateLinePrice(lineEl, item) {
    const priceWrap = lineEl.querySelector('.dtc-cart-drawer__price');
    if (!priceWrap) return;

    if (item.original_price !== item.final_price) {
      const oldPrice = priceWrap.querySelector('.dtc-cart-drawer__old-price');
      const finalPrice = priceWrap.querySelector('.cart-item__final-price');
      if (oldPrice && finalPrice) {
        oldPrice.textContent = this.formatMoney(item.original_price);
        finalPrice.textContent = this.formatMoney(item.final_price);
        return;
      }

      priceWrap.innerHTML = `<span class="sr-only">Regular price</span><s class="cart-item__old-price dtc-cart-drawer__old-price">${this.formatMoney(item.original_price)}</s><span class="sr-only">Sale price</span><span class="cart-item__final-price">${this.formatMoney(item.final_price)}</span>`;
      return;
    }

    priceWrap.textContent = this.formatMoney(item.original_price);
  },

  updateCartBubble(cart) {
    const bubble = document.querySelector('.dtc-header #cart-icon-bubble');
    if (!bubble) return;

    const countWrap = bubble.querySelector('.dtc-header__cart-count');
    if (!countWrap) return;

    countWrap.classList.toggle('dtc-header__cart-count--empty', cart.item_count === 0);
    countWrap.classList.toggle('dtc-header__cart-count--wide', cart.item_count >= 100);

    let countSpan = countWrap.querySelector('[aria-hidden="true"]');
    if (cart.item_count > 0) {
      if (!countSpan) {
        countSpan = document.createElement('span');
        countSpan.setAttribute('aria-hidden', 'true');
        countWrap.appendChild(countSpan);
      }
      countSpan.textContent = cart.item_count < 100 ? String(cart.item_count) : '99+';
      return;
    }

    if (countSpan) countSpan.remove();
  },

  async syncCartBubble() {
    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const cart = await response.json();
      this.updateCartBubble(cart);
    } catch {
      // Ignore bubble sync failures; drawer state remains authoritative.
    }
  },

  async _fetchSections() {
    const response = await fetch(`${window.location.pathname}?sections=${this.sectionName}`);
    return response.json();
  },

  clearError() {
    const errors = document.getElementById('DtcCartDrawer-CartErrors');
    if (errors) errors.textContent = '';
  },

  refreshDrawerContents(nextDrawer, currentDrawer) {
    const wasActive = currentDrawer.classList.contains('active');

    ['.dtc-cart-drawer__items', '.dtc-cart-drawer__footer'].forEach((selector) => {
      const target = currentDrawer.querySelector(selector);
      const source = nextDrawer.querySelector(selector);
      if (target && source) {
        target.replaceWith(source);
      }
    });

    currentDrawer.classList.toggle('is-empty', nextDrawer.classList.contains('is-empty'));

    const main = currentDrawer.querySelector('.dtc-cart-drawer__main');
    const nextMain = nextDrawer.querySelector('.dtc-cart-drawer__main');
    if (main && nextMain) {
      main.classList.toggle('is-empty', nextMain.classList.contains('is-empty'));
    }

    if (wasActive) {
      currentDrawer.classList.add('active');
      document.body.classList.add('overflow-hidden');
    }

    if (typeof currentDrawer.syncQuantityInputs === 'function') {
      currentDrawer.syncQuantityInputs();
    }
  },

  async refresh(sections) {
    let sectionHtml = sections && sections[this.sectionName];

    if (!sectionHtml) {
      const response = await fetch(`${window.location.pathname}?sections=${this.sectionName}`);
      const data = await response.json();
      sectionHtml = data[this.sectionName];
    }

    if (!sectionHtml) return;

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const nextDrawer = doc.querySelector('dtc-cart-drawer');
    const currentDrawer = document.querySelector('dtc-cart-drawer');

    if (nextDrawer && currentDrawer) {
      this.refreshDrawerContents(nextDrawer, currentDrawer);
    }

    await this.syncCartBubble();
  },

  open(options = {}) {
    const drawer = document.querySelector('dtc-cart-drawer');
    if (drawer) drawer.open(options);
  },

  showError(message) {
    const errors = document.getElementById('DtcCartDrawer-CartErrors');
    if (errors) errors.textContent = message;
  },

  getVariantId(trigger) {
    if (trigger.dataset.variantId) return trigger.dataset.variantId;

    const form = trigger.closest('form');
    if (!form) return null;

    const checkedInput = form.querySelector('input[name="id"]:checked');
    if (checkedInput) return checkedInput.value;

    const variantInput = form.querySelector('select[name="id"], input[name="id"]');
    return variantInput ? variantInput.value : null;
  },

  getQuantity(trigger) {
    if (trigger.dataset.quantity) return Math.max(Number(trigger.dataset.quantity), 1);

    const form = trigger.closest('form');
    const quantityInput = form
      ? form.elements.namedItem('quantity') || form.querySelector('[name="quantity"]')
      : null;
    const quantity = quantityInput ? Number(quantityInput.value) : 1;

    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  },

  resolveAddToCartGtm(trigger) {
    const readAttr = (el, key) => ((el && el.getAttribute('data-v-gtm-' + key)) || '').trim();
    const form = trigger.closest('form[data-dtc-cart-drawer-form]') || trigger.closest('form');
    const listRoot = trigger.closest('[data-v-gtm-item-list]');
    const module =
      readAttr(trigger, 'module') ||
      readAttr(form, 'module') ||
      (form && form.getAttribute('data-v-gtm-module')) ||
      readAttr(listRoot, 'module') ||
      (trigger.closest('[data-v-gtm-module]') &&
        readAttr(trigger.closest('[data-v-gtm-module]'), 'module')) ||
      (form && form.closest('product-form, [data-section-type="main-product"]')
        ? 'Product Details Page'
        : 'Product List');

    const buttonName =
      readAttr(trigger, 'button_name') ||
      (
        trigger.querySelector(
          '.dtc-product-form__atc-btn-text, .dtc-product-card__atc-btn-text, [name="add"] > span'
        ) || {}
      ).textContent ||
      trigger.textContent ||
      'Add to Cart';

    return {
      module: module,
      button_name: String(buttonName).trim().replace(/\s+/g, ' '),
      item_list_id:
        readAttr(trigger, 'item-list-id') ||
        readAttr(listRoot, 'item-list-id') ||
        module,
      item_list_name:
        readAttr(trigger, 'item-list-name') ||
        readAttr(listRoot, 'item-list-name') ||
        module,
      index: parseInt(readAttr(trigger, 'index'), 10) || 0,
    };
  },
};

function enhanceDtcCartIcon(scope = document) {
  const cartLink = scope.querySelector('.dtc-header #cart-icon-bubble');
  if (!cartLink || cartLink.dataset.dtcCartEnhanced === 'true') return;

  cartLink.dataset.dtcCartEnhanced = 'true';
  cartLink.setAttribute('role', 'button');
  cartLink.setAttribute('aria-haspopup', 'dialog');
}

function openDtcCartFromIcon(event) {
  const cartLink = event.target.closest('#cart-icon-bubble');
  if (!cartLink || !cartLink.closest('.dtc-header')) return;
  if (!document.querySelector('dtc-cart-drawer')) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  window.DtcCartDrawerController.open();
}

document.addEventListener('click', openDtcCartFromIcon, true);
document.addEventListener('keydown', (event) => {
  const cartLink = event.target.closest('#cart-icon-bubble');
  if (!cartLink || !cartLink.closest('.dtc-header')) return;
  if (!document.querySelector('dtc-cart-drawer')) return;

  if (event.code.toUpperCase() === 'SPACE' || event.code === 'Enter') {
    event.preventDefault();
    window.DtcCartDrawerController.open();
  }
});

enhanceDtcCartIcon();

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = document.getElementById('shopify-section-' + event.detail.sectionId);
    if (section) enhanceDtcCartIcon(section);
  });
}

function resolveDtcAddToCartGtm(trigger) {
  return window.DtcCartDrawerController.resolveAddToCartGtm(trigger);
}

function shouldUseDtcCartDrawer(form) {
  return (
    window.DtcCartDrawerController &&
    document.querySelector('dtc-cart-drawer') &&
    form &&
    !form.querySelector('[name="properties[__shopify_send_gift_card_to_recipient]"]')
  );
}

async function addToCartViaDtcDrawer(trigger, form) {
  const controller = window.DtcCartDrawerController;
  const variantId = controller.getVariantId(trigger);

  if (!variantId) {
    controller.showError('Please select a product option before adding to cart.');
    controller.open();
    return { ok: false, handled: true };
  }

  const quantity = controller.getQuantity(trigger);
  const gtmContext = controller.resolveAddToCartGtm(trigger);

  try {
    window.__vGtmSuppressFetchCartAdd = true;
    window.__vGtmSuppressDrawerViewCart = true;
    closeLegacyCartDrawer();
    const lineItem = await controller.add(variantId, quantity);
    if (lineItem && window.vGtm && window.vGtm.cart && typeof window.vGtm.cart.trackAdd === 'function') {
      window.vGtm.cart.trackAdd(lineItem, quantity, gtmContext);
    }
    return { ok: true, handled: true, lineItem, variantId, quantity };
  } catch (error) {
    controller.showError(error.message);
    controller.open();
    return { ok: false, handled: true, error };
  } finally {
    window.__vGtmSuppressFetchCartAdd = false;
    window.__vGtmSuppressDrawerViewCart = false;
  }
}

function closeLegacyCartDrawer() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;

  if (typeof drawer.close === 'function') {
    drawer.close();
    return;
  }

  drawer.classList.remove('active', 'animate');
  document.body.classList.remove('overflow-hidden');
}

function getLegacyCartHost(form) {
  return form.closest('product-form, quick-add-to-cart, card-product-quick-add');
}

function getLegacyCartTrigger(host, form, fallbackTarget) {
  return (
    host?.submitButton ||
    host?.addButton ||
    form.querySelector('[type="submit"], [name="add"]') ||
    fallbackTarget
  );
}

function setLegacyCartLoading(host, loading) {
  if (!host) return;

  const button = getLegacyCartTrigger(host, host.form || host.querySelector('form'));
  const spinner = host.querySelector('.loader-spinner');

  if (!button) return;

  if (loading) {
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');
    if (spinner) spinner.classList.remove('hidden');
    return;
  }

  button.classList.remove('is-loading');
  if (!host.error) button.removeAttribute('aria-disabled');
  if (spinner) spinner.classList.add('hidden');
}

async function handleLegacyCartAdd(host, form, trigger) {
  if (!trigger || trigger.getAttribute('aria-disabled') === 'true') return;
  if (host.__dtcCartAddInFlight) return;

  host.__dtcCartAddInFlight = true;
  host.handleErrorMessage?.();
  setLegacyCartLoading(host, true);

  try {
    const result = await addToCartViaDtcDrawer(trigger, form);

    if (result.ok) {
      host.error = false;
      const quickAddModal = host.closest('quick-add-modal');
      if (quickAddModal && typeof quickAddModal.hide === 'function') {
        quickAddModal.hide(true);
      }
    } else if (result.error) {
      host.handleErrorMessage?.(result.error.message);
      host.error = true;
    }
  } finally {
    host.__dtcCartAddInFlight = false;
    setLegacyCartLoading(host, false);
  }
}

async function handleDtcAddToCart(trigger) {
  if (trigger.disabled || trigger.classList.contains('is-loading')) return;

  trigger.classList.add('is-loading');
  trigger.setAttribute('aria-busy', 'true');

  const form = trigger.closest('form');
  const result = await addToCartViaDtcDrawer(trigger, form);

  trigger.classList.remove('is-loading');
  trigger.removeAttribute('aria-busy');

  return result;
}

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-dtc-cart-drawer-form]');
  if (!form) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  event.stopPropagation();

  const trigger = event.submitter || form.querySelector('[data-dtc-add-to-cart], [type="submit"]') || form;
  await handleDtcAddToCart(trigger);
}, true);

document.addEventListener('click', async (event) => {
  const trigger = event.target.closest('[data-dtc-add-to-cart]');
  if (!trigger) return;
  if (trigger.type === 'submit' && trigger.closest('form[data-dtc-cart-drawer-form]')) return;

  event.preventDefault();
  event.stopPropagation();

  await handleDtcAddToCart(trigger);
});

(() => {
  /**
   * DTC product form — description line-clamp + expand toggle (Figma 10269:44385)
   */
  function initDtcProductDescriptions(scope) {
    const root = scope || document;
    root.querySelectorAll("[data-dtc-product-description]").forEach((wrap) => {
      if (wrap.dataset.dtcDescriptionInit === "true") return;
      wrap.dataset.dtcDescriptionInit = "true";

      const content = wrap.querySelector(".dtc-product-form__description");
      const toggle = wrap.querySelector(".dtc-product-form__description-toggle");
      if (!content || !toggle) return;

      const labelMore = toggle.dataset.labelMore || "Show more";
      const labelLess = toggle.dataset.labelLess || "Show less";

      function measureOverflow() {
        content.classList.add("is-clamped");
        return content.scrollHeight > content.clientHeight + 1;
      }

      function updateToggleVisibility() {
        if (wrap.dataset.expanded === "true") return;

        const overflows = measureOverflow();
        toggle.hidden = !overflows;

        if (!overflows) {
          content.classList.remove("is-clamped");
          toggle.setAttribute("aria-expanded", "false");
          syncToggleChrome(false, { settled: false, showEllipsis: false });
        } else if (wrap.dataset.expanded !== "true") {
          syncToggleChrome(false, { settled: false, showEllipsis: true });
        }
      }

      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      const ELLIPSIS_REVEAL_DELAY_MS = 140;
      let descriptionHeightTransitionCleanup = null;
      let ellipsisRevealTimer = null;

      function clearDescriptionHeight() {
        content.style.maxHeight = "";
        content.classList.remove("is-collapsing");
      }

      function setEllipsisVisible(visible, delayMs = 0) {
        clearTimeout(ellipsisRevealTimer);
        if (!visible) {
          toggle.classList.remove("is-ellipsis-visible");
          return;
        }
        if (delayMs <= 0) {
          toggle.classList.add("is-ellipsis-visible");
          return;
        }
        ellipsisRevealTimer = setTimeout(() => {
          if (wrap.dataset.expanded !== "true") {
            toggle.classList.add("is-ellipsis-visible");
          }
        }, delayMs);
      }

      function syncToggleChrome(expanded, { settled = expanded, showEllipsis = !expanded } = {}) {
        toggle.classList.toggle("is-expanded", expanded);
        toggle.classList.toggle("is-toggle-settled", settled);
        if (showEllipsis) {
          setEllipsisVisible(true, 0);
        } else {
          setEllipsisVisible(false);
        }
      }

      function cleanupDescriptionHeightTransition(options = {}) {
        const { revealEllipsis = false } = options;
        clearTimeout(ellipsisRevealTimer);
        if (!descriptionHeightTransitionCleanup) return;
        descriptionHeightTransitionCleanup();
        descriptionHeightTransitionCleanup = null;
        const expanded = wrap.dataset.expanded === "true";
        content.classList.toggle("is-clamped", !expanded);
        clearDescriptionHeight();
        syncToggleChrome(expanded, { showEllipsis: false });
        if (revealEllipsis && !expanded) {
          setEllipsisVisible(true, ELLIPSIS_REVEAL_DELAY_MS);
        }
      }

      function getClampHeight() {
        const wasClamped = content.classList.contains("is-clamped");
        content.classList.add("is-clamped");
        clearDescriptionHeight();
        const height = content.clientHeight;
        if (!wasClamped) content.classList.remove("is-clamped");
        return height;
      }

      function animateDescriptionHeight(expanded) {
        cleanupDescriptionHeightTransition();

        if (prefersReducedMotion) {
          content.classList.toggle("is-clamped", !expanded);
          clearDescriptionHeight();
          syncToggleChrome(expanded);
          return;
        }

        if (expanded) {
          setEllipsisVisible(false);
          toggle.classList.add("is-expanded");
          toggle.classList.remove("is-toggle-settled");

          const clampHeight = getClampHeight();
          content.classList.remove("is-clamped", "is-collapsing");
          const fullHeight = content.scrollHeight;

          content.style.maxHeight = clampHeight + "px";
          content.offsetHeight;
          content.style.maxHeight = fullHeight + "px";

          const onExpandEnd = (event) => {
            if (event.target !== content || event.propertyName !== "max-height") {
              return;
            }
            cleanupDescriptionHeightTransition();
          };
          content.addEventListener("transitionend", onExpandEnd);
          descriptionHeightTransitionCleanup = () => {
            content.removeEventListener("transitionend", onExpandEnd);
            clearDescriptionHeight();
          };
          return;
        }

        setEllipsisVisible(false);
        toggle.classList.remove("is-expanded", "is-toggle-settled");

        const clampHeight = getClampHeight();
        const fullHeight = content.scrollHeight;

        if (fullHeight <= clampHeight + 1) {
          content.classList.add("is-clamped");
          clearDescriptionHeight();
          syncToggleChrome(false, { showEllipsis: true });
          return;
        }

        content.classList.remove("is-clamped", "is-collapsing");
        content.style.maxHeight = fullHeight + "px";
        content.offsetHeight;
        content.classList.add("is-collapsing");
        content.style.maxHeight = clampHeight + "px";

        const onCollapseEnd = (event) => {
          if (event.target !== content || event.propertyName !== "max-height") {
            return;
          }
          cleanupDescriptionHeightTransition({ revealEllipsis: true });
        };
        content.addEventListener("transitionend", onCollapseEnd);
        descriptionHeightTransitionCleanup = () => {
          content.removeEventListener("transitionend", onCollapseEnd);
          clearDescriptionHeight();
        };
      }

      function setExpanded(expanded, options = {}) {
        const { animate = true } = options;
        wrap.dataset.expanded = expanded ? "true" : "false";
        toggle.setAttribute("aria-expanded", String(expanded));
        toggle.setAttribute("aria-label", expanded ? labelLess : labelMore);

        if (animate && !prefersReducedMotion) {
          animateDescriptionHeight(expanded);
        } else {
          content.classList.toggle("is-clamped", !expanded);
          clearDescriptionHeight();
          syncToggleChrome(expanded);
        }
      }

      setExpanded(false, { animate: false });
      updateToggleVisibility();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          wrap.classList.add("is-description-chrome-ready");
        });
      });

      let resizeTimer;
      window.addEventListener("resize", () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(updateToggleVisibility, 100);
      });

      toggle.addEventListener("click", () => {
        setExpanded(wrap.dataset.expanded !== "true", { animate: true });
      });
    });
  }

  /**
   * DTC buy buttons — show Amazon CTA below Sold out ATC when variant is unavailable
   */
  function initDtcSoldOutAmazon(scope) {
    const root = scope || document;
    root.querySelectorAll("[data-dtc-sold-out-actions]").forEach((soldOutActions) => {
      if (soldOutActions.dataset.dtcSoldOutAmazonInit === "true") return;
      soldOutActions.dataset.dtcSoldOutAmazonInit = "true";

      const section =
        soldOutActions.closest(".shopify-section") ||
        soldOutActions.closest("section");
      if (!section) return;

      const variantRadios = section.querySelector("variant-radios");
      if (!variantRadios) return;

      const variantsScript = variantRadios.querySelector(
        'script[type="application/json"]'
      );
      if (!variantsScript) return;

      let variants;
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch {
        return;
      }

      function getSelectedVariant() {
        const fieldsets = Array.from(variantRadios.querySelectorAll("fieldset"));
        const options = fieldsets.map((fieldset) => {
          const checked = Array.from(fieldset.querySelectorAll("input")).find(
            (input) => input.checked
          );
          return checked ? checked.value : null;
        });

        if (options.includes(null)) return null;

        return variants.find(
          (variant) =>
            !variant.options
              .map((option, index) => options[index] === option)
              .includes(false)
        );
      }

      function updateSoldOutActions() {
        const variant = getSelectedVariant();
        const isSoldOut = Boolean(variant && variant.available === false);
        soldOutActions.hidden = !isSoldOut;
      }

      variantRadios.addEventListener("change", updateSoldOutActions);
      updateSoldOutActions();
    });
  }

  /**
   * DTC PDP gallery — thumbs vertical nav arrows
   */
  function initDtcThumbsNav(scope) {
    const root = scope || document;
    root.querySelectorAll("product-gallery-slider").forEach((gallery) => {
      if (gallery.dataset.dtcThumbsNavInit === "true") return;
      gallery.dataset.dtcThumbsNavInit = "true";

      const prevBtn = gallery.querySelector("[thumbs-button-prev]");
      const nextBtn = gallery.querySelector("[thumbs-button-next]");
      if (!prevBtn || !nextBtn) return;

      function waitForThumbs() {
        if (gallery.thumbs && gallery.thumbs.initialized) {
          bindNav(gallery.thumbs);
        } else {
          requestAnimationFrame(waitForThumbs);
        }
      }

      function bindNav(thumbsSwiper) {
        function updateBtnState() {
          prevBtn.classList.toggle("swiper-button-disabled", thumbsSwiper.isBeginning);
          prevBtn.disabled = thumbsSwiper.isBeginning;
          nextBtn.classList.toggle("swiper-button-disabled", thumbsSwiper.isEnd);
          nextBtn.disabled = thumbsSwiper.isEnd;
        }

        prevBtn.addEventListener("click", () => {
          const current = thumbsSwiper.activeIndex;
          thumbsSwiper.slideTo(Math.max(0, current - 1));
        });

        nextBtn.addEventListener("click", () => {
          thumbsSwiper.slideTo(thumbsSwiper.activeIndex + 1);
        });

        thumbsSwiper.on("slideChange", updateBtnState);
        thumbsSwiper.on("reachBeginning", updateBtnState);
        thumbsSwiper.on("reachEnd", updateBtnState);
        thumbsSwiper.on("fromEdge", updateBtnState);
        updateBtnState();
      }

      waitForThumbs();
    });
  }

  /**
   * dtc-video upload branch — video-media + play button (aligns with sections/video.liquid)
   */
  if (!customElements.get("video-media")) {
    class DtcVideoMedia extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector("video");
        if (!this.video) return;

        if (this.hasAttribute("autoplay")) {
          this.markLoaded();
          this.video.play().catch(() => {});
          return;
        }

        this.addEventListener("click", this.onActivate.bind(this));
        this.video.addEventListener("play", () => this.markLoaded());
      }

      onActivate(event) {
        if (this.hasAttribute("loaded")) return;
        if (event.target.closest("[data-dtc-video-play]")) return;
        this.playFromUser();
      }

      markLoaded() {
        this.setAttribute("loaded", "");
      }

      playFromUser() {
        if (!this.video) return;
        this.markLoaded();
        this.video.controls = true;
        const playBtn = this.closest(".dtc-video--upload")?.querySelector(
          "[data-dtc-video-play]"
        );
        if (playBtn) playBtn.hidden = true;
        return this.video.play();
      }

      play() {
        return this.playFromUser();
      }
    }

    customElements.define("video-media", DtcVideoMedia);
  }

  function getActiveVideoMedia(container) {
    const candidates = container.querySelectorAll("video-media");
    for (const media of candidates) {
      if (media.offsetParent !== null) return media;
    }
    return candidates[0] || null;
  }

  function initDtcVideoUpload(scope) {
    const root = scope || document;
    root.querySelectorAll(".dtc-video--upload").forEach((container) => {
      if (container.dataset.dtcVideoInit === "true") return;
      container.dataset.dtcVideoInit = "true";

      const playBtn = container.querySelector("[data-dtc-video-play]");
      if (!playBtn) return;

      playBtn.addEventListener("click", () => {
        const media = getActiveVideoMedia(container);
        if (!media) return;

        const playPromise =
          typeof media.play === "function" ? media.play() : null;

        if (playPromise && typeof playPromise.then === "function") {
          playPromise.then(() => {
            playBtn.hidden = true;
          }).catch(() => {});
        } else {
          playBtn.hidden = true;
        }
      });
    });
  }

  initDtcProductDescriptions();
  initDtcSoldOutAmazon();
  initDtcThumbsNav();
  initDtcVideoUpload();

  /**
   * Prevent FOUC on Shopify Dynamic Checkout buttons.
   * The wrapper starts with .dtc-dynamic-checkout--loading (visibility:hidden).
   * We watch for Shopify's async JS to inject the real button DOM, then reveal it.
   */
  function initDtcDynamicCheckout(scope) {
    const root = scope || document;
    root.querySelectorAll(".dtc-dynamic-checkout--loading").forEach((wrapper) => {
      const paymentBtn = wrapper.querySelector(".shopify-payment-button");
      if (!paymentBtn) {
        wrapper.classList.remove("dtc-dynamic-checkout--loading");
        return;
      }

      // Shopify injects real buttons asynchronously — reveal once any button appears
      const observer = new MutationObserver(() => {
        if (paymentBtn.querySelector("button, [role='button']")) {
          observer.disconnect();
          wrapper.classList.remove("dtc-dynamic-checkout--loading");
        }
      });
      observer.observe(paymentBtn, { childList: true, subtree: true });

      // Safety fallback: always reveal after 2s regardless
      setTimeout(() => {
        observer.disconnect();
        wrapper.classList.remove("dtc-dynamic-checkout--loading");
      }, 2000);
    });
  }

  initDtcDynamicCheckout();

  if (typeof Shopify !== "undefined" && Shopify.designMode) {
    document.addEventListener("shopify:section:load", (event) => {
      const section = document.getElementById(
        "shopify-section-" + event.detail.sectionId
      );
      if (!section) return;

      section
        .querySelectorAll("[data-dtc-product-description]")
        .forEach((wrap) => {
          delete wrap.dataset.dtcDescriptionInit;
        });

      section.querySelectorAll("[data-dtc-sold-out-actions]").forEach((el) => {
        delete el.dataset.dtcSoldOutAmazonInit;
      });

      section.querySelectorAll("product-gallery-slider").forEach((el) => {
        delete el.dataset.dtcThumbsNavInit;
      });

      setTimeout(() => {
        initDtcProductDescriptions(section);
        initDtcSoldOutAmazon(section);
        initDtcThumbsNav(section);
        initDtcDynamicCheckout(section);
        initDtcVideoUpload(section);
      }, 100);
    });
  }
})();

(function interceptLegacyProductFormsForDtcCart() {
  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.closest('[data-dtc-cart-drawer-form]')) return;

      const host = getLegacyCartHost(form);
      if (!host || !shouldUseDtcCartDrawer(form)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const trigger = event.submitter || getLegacyCartTrigger(host, form);
      await handleLegacyCartAdd(host, form, trigger);
    },
    true
  );

  document.addEventListener(
    'click',
    async (event) => {
      const addButton = event.target.closest('card-product-quick-add [name="add"]');
      if (!addButton) return;

      const host = addButton.closest('card-product-quick-add');
      const form = host?.form || host?.querySelector('form');
      if (!form || !shouldUseDtcCartDrawer(form)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      await handleLegacyCartAdd(host, form, addButton);
    },
    true
  );
})();
